import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame } from "lucide-react";
import { api } from "../api";
import { SEO } from "../components/SEO";
import { usePageContent, resolveMediaUrl } from "../components/PageContent";
import { usePageBlocks, PageBlocksRenderer } from "../components/PageBlocks";

const RISK_COLORS = {
  LOW: "bg-card-light",
  MEDIUM: "bg-sand",
  HIGH: "bg-terra text-bone",
  CATASTROPHIC: "bg-asphalt text-bone",
};

export default function BadIdeas() {
  const [items, setItems] = useState([]);
  const c = usePageContent("bad-ideas", {
    eyebrow: "Bureau memos",
    title: "Bad Ideas",
    body: 'Filed under "things we will probably regret." Rated by risk. Approved by no one.',
  });
  const blocks = usePageBlocks("bad-ideas");
  useEffect(() => {
    api.get("/bad-ideas").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  const bg = resolveMediaUrl(c.image);

  return (
    <div className="bg-bone min-h-screen" data-testid="bad-ideas-page">
      <SEO title={c.title || "Bad Ideas"} description={c.body} image={bg} path="/bad-ideas" />
      <section
        className="bg-terra text-bone py-16 relative overflow-hidden"
        style={
          c.show_media && bg
            ? { backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" }
            : undefined
        }
        data-testid="bad-ideas-hero"
      >
        {!bg && <div className="absolute inset-0 halftone opacity-10 pointer-events-none" />}
        {c.show_media && bg && c.overlay && (
          <div className="absolute inset-0 bg-terra/80 mix-blend-multiply pointer-events-none" />
        )}
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 relative">
          {c.eyebrow && <div className="font-mono text-xs uppercase tracking-[0.35em] mb-3">✦ {c.eyebrow}</div>}
          <h1 className="font-display text-6xl sm:text-7xl uppercase leading-none tracking-tighter whitespace-pre-line">{c.title}</h1>
          {c.subtitle && <p className="font-western text-2xl mt-4 max-w-2xl">{c.subtitle}</p>}
          {c.body && <p className="font-western text-2xl mt-4 max-w-2xl whitespace-pre-line">{c.body}</p>}
        </div>
      </section>
      <div className="tape-divider" />
      <PageBlocksRenderer blocks={blocks} testid="bad-ideas-blocks" />
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
        <div className="grid md:grid-cols-2 gap-8">
          {items.map((b, i) => (
            <Link
              to={`/bad-ideas/${b.slug}`}
              key={b.id}
              data-testid={`bad-idea-card-${i}`}
              className="block border-2 border-asphalt bg-bone shadow-hard hover:-translate-y-1 hover:-translate-x-1 hover:shadow-hard-lg transition-all"
            >
              <div className="aspect-[16/9] overflow-hidden border-b-2 border-asphalt bg-asphalt">
                {b.cover_image && <img src={resolveMediaUrl(b.cover_image)} alt={b.title} className="w-full h-full object-cover ink-stamp" />}
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink">Memo №. {String(i + 1).padStart(3, "0")}</span>
                  <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 border-2 border-asphalt ${RISK_COLORS[b.risk_level] || ""}`}>
                    <Flame className="w-3 h-3" /> {b.risk_level}
                  </span>
                </div>
                <h3 className="font-display text-3xl uppercase tracking-tight leading-tight">{b.title}</h3>
                <p className="font-western text-xl text-terra mt-1">{b.tagline}</p>
                <p className="font-body text-sm text-ink mt-3 line-clamp-3">{b.body}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
